import React, { useCallback, useMemo } from "react"
import { getFont } from "../../utils/FontFactor"

interface AutoFitSvgTextProps {
  text: string
  width?: number
  height?: number
  minFontSize?: number
  padding?: number
  lineHeight?: number
}

const MAX_WORD_LENGTH = 24

const AutoFitSvgText: React.FC<AutoFitSvgTextProps> = ({
  text,
  width = 100,
  height = 100,
  minFontSize = 10,
  padding = 8,
  lineHeight = 1.2,
}) => {
  const words = useMemo(() => {
    const regex = new RegExp(`.{1,${MAX_WORD_LENGTH}}`, "g")
    return text.split(" ").flatMap((word) => (word.length >= MAX_WORD_LENGTH ? word.match(regex) || [] : [word]))
  }, [text])

  const measureTextLines = useCallback(
    (fontSize: number): string[] => {
    const maxWidth = width - padding * 2
      const lines: string[] = []
      let currentLine = ""

      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word
        const approxWidth = testLine.length * fontSize * 0.55
        if (approxWidth < maxWidth) {
          currentLine = testLine
        } else {
          if (currentLine) lines.push(currentLine)
          currentLine = word
        }
      })
      if (currentLine) lines.push(currentLine)
      return lines
    },
    [words, width, padding]
  )

  const bestFit = useMemo(() => {
    let fontSize = getFont(words)
    let lines = measureTextLines(fontSize)
    let maxLines = Math.max(1, Math.floor((height - padding * 2) / (fontSize * lineHeight)))

    while (lines.length > maxLines && fontSize > minFontSize) {
      fontSize -= 1
      lines = measureTextLines(fontSize)
      maxLines = Math.max(1, Math.floor((height - padding * 2) / (fontSize * lineHeight)))
    }

    return { fontSize, lines }
  }, [height, minFontSize, lineHeight, padding, measureTextLines, words])

  const { fontSize, lines } = bestFit

  const yStart = padding

  return (
    <>
      {lines.map((line, i) => (
        <text key={i} x={padding} y={yStart + i * fontSize * lineHeight} fontSize={fontSize} textAnchor="start" dominantBaseline="hanging">
          {line}
        </text>
      ))}
    </>
  )
}

export default AutoFitSvgText
